import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import {
  LoginRequest, LoginResponse, AuthUser, RegisterRequest, CorporateRegisterRequest,
  MembershipRegisterRequest, TokenRefreshRequest, ChangePasswordRequest, ForgotPasswordRequest,
  VerifyOtpRequest, ResetPasswordRequest, AccountDeactivateRequest,
  OtpResponse, CheckUsernameResponse, SuggestUsernamesResponse
} from '../models/auth.model';
import { ApiResponse } from '../models/api.model';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http   = inject(HttpClient);
  private router = inject(Router);

  private readonly TOKEN_KEY   = 'pss_token';
  private readonly REFRESH_KEY = 'pss_refresh';
  private readonly USER_KEY    = 'pss_user';
  private readonly SESSION_KEY = 'pss_session';
  private readonly ACTIVE_MODULE_KEY = 'pss_active_module';
  private readonly channel = new BroadcastChannel('pss_auth');

  private _user = signal<AuthUser | null>(this.loadUser());
  readonly user       = this._user.asReadonly();
  readonly isLoggedIn = computed(() => !!this._user());
  readonly userRoles  = computed(() => this._user()?.roles ?? []);
  readonly isAdmin    = computed(() => {
    const roles = this._user()?.roles ?? [];
    return roles.includes('Admin') || roles.includes('Super Admin') || roles.includes('Pit Stop Staff');
  });

  constructor() {
    // Cross-tab sync: another tab logged in or logged out.
    // IMPORTANT: only clear in-memory state here — do NOT call clearSession(), which
    // would delete shared localStorage keys that the other tab already owns:
    //   'login'  → that tab just wrote a new session; deleting it would wipe its session.
    //   'logout' → that tab already removed the keys; calling clearSession() would also
    //              re-broadcast causing a loop.
    this.channel.onmessage = (e: MessageEvent<{ type: string }>) => {
      if ((e.data.type === 'login' || e.data.type === 'logout') && this._user() !== null) {
        this._user.set(null);
        this.router.navigate(['/app/auth/login']);
      }
    };
  }

  // ── Auth ──────────────────────────────────────────────────────────────
  login(req: LoginRequest): Observable<ApiResponse<LoginResponse>> {
    return this.http.post<ApiResponse<LoginResponse>>(`${environment.apiUrl}/auth/login`, req).pipe(
      tap(res => { if (res.success) this.storeSession(res.data); })
    );
  }

  register(req: RegisterRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${environment.apiUrl}/auth/register`, req);
  }

  registerCorporate(req: CorporateRegisterRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${environment.apiUrl}/auth/register/corporate`, req);
  }

  registerMembership(req: MembershipRegisterRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${environment.apiUrl}/auth/register/membership`, req);
  }

  refreshToken(): Observable<ApiResponse<LoginResponse>> {
    const body: TokenRefreshRequest = {
      accessToken:  localStorage.getItem(this.TOKEN_KEY)   ?? '',
      refreshToken: localStorage.getItem(this.REFRESH_KEY) ?? ''
    };
    return this.http.post<ApiResponse<LoginResponse>>(`${environment.apiUrl}/auth/refresh-token`, body).pipe(
      tap(res => { if (res.success) this.storeSession(res.data); })
    );
  }

  logout(): void {
    this.http.post(`${environment.apiUrl}/auth/logout`, {}).subscribe();
    this.clearSession();
  }

  clearSession(): void {
    // Notify other tabs before clearing so they can react while user is still set
    if (this._user() !== null) {
      this.channel.postMessage({ type: 'logout' });
    }
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.SESSION_KEY);
    localStorage.removeItem(this.ACTIVE_MODULE_KEY);
    this._user.set(null);
    this.router.navigate(['/app/auth/login']);
  }

  // ── Password ──────────────────────────────────────────────────────────
  changePassword(req: ChangePasswordRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${environment.apiUrl}/auth/change-password`, req);
  }
  sendChangePasswordOtp(): Observable<ApiResponse<OtpResponse>> {
    return this.http.post<ApiResponse<OtpResponse>>(`${environment.apiUrl}/auth/change-password/send-otp`, {});
  }
  forgotPassword(req: ForgotPasswordRequest): Observable<ApiResponse<OtpResponse>> {
    return this.http.post<ApiResponse<OtpResponse>>(`${environment.apiUrl}/auth/forgot-password`, req);
  }

  resetPassword(req: ResetPasswordRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${environment.apiUrl}/auth/reset-password`, req);
  }

  // ── OTP ───────────────────────────────────────────────────────────────
  sendOtp(identifier: string, purpose: string): Observable<ApiResponse<OtpResponse>> {
    return this.http.post<ApiResponse<OtpResponse>>(`${environment.apiUrl}/auth/send-otp`, { identifier, purpose });
  }

  verifyOtp(req: VerifyOtpRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${environment.apiUrl}/auth/verify-otp`, req);
  }

  // ── MFA ───────────────────────────────────────────────────────────────
  getMfaStatus(): Observable<ApiResponse<{ enabled: boolean; method: string | null }>> {
    return this.http.get<ApiResponse<{ enabled: boolean; method: string | null }>>(`${environment.apiUrl}/auth/mfa/status`);
  }

  setupMfa(method: string): Observable<ApiResponse<{ secret: string; qrCodeUri: string; maskedTarget: string }>> {
    return this.http.post<ApiResponse<{ secret: string; qrCodeUri: string; maskedTarget: string }>>(`${environment.apiUrl}/auth/mfa/setup`, { method });
  }

  confirmMfaSetup(code: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${environment.apiUrl}/auth/mfa/confirm`, { code });
  }

  disableMfa(password: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${environment.apiUrl}/auth/mfa/disable`, { password });
  }

  verifyMfa(code: string, sessionToken: string): Observable<ApiResponse<LoginResponse>> {
    return this.http.post<ApiResponse<LoginResponse>>(`${environment.apiUrl}/auth/mfa/verify`, { code, sessionToken }).pipe(
      tap(res => { if (res.success) this.storeSession(res.data); })
    );
  }

  // ── Username / Phone / Email availability ────────────────────────────
  checkUsername(username: string): Observable<ApiResponse<CheckUsernameResponse>> {
    return this.http.get<ApiResponse<CheckUsernameResponse>>(`${environment.apiUrl}/auth/check-username?username=${encodeURIComponent(username)}`);
  }

  checkPhone(phone: string): Observable<ApiResponse<CheckUsernameResponse>> {
    return this.http.get<ApiResponse<CheckUsernameResponse>>(`${environment.apiUrl}/auth/check-phone?phone=${encodeURIComponent(phone)}`);
  }

  checkEmail(email: string): Observable<ApiResponse<CheckUsernameResponse>> {
    return this.http.get<ApiResponse<CheckUsernameResponse>>(`${environment.apiUrl}/auth/check-email?email=${encodeURIComponent(email)}`);
  }

  suggestUsernames(name: string): Observable<ApiResponse<SuggestUsernamesResponse>> {
    return this.http.get<ApiResponse<SuggestUsernamesResponse>>(`${environment.apiUrl}/auth/suggest-usernames?name=${encodeURIComponent(name)}`);
  }

  // ── Account ───────────────────────────────────────────────────────────
  activateAccount(token: string): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${environment.apiUrl}/auth/activate`, { token });
  }

  deactivateAccount(req: AccountDeactivateRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(`${environment.apiUrl}/auth/deactivate`, req);
  }

  // ── Device ────────────────────────────────────────────────────────────
  getTrustedDevices(): Observable<ApiResponse<any[]>> {
    return this.http.get<ApiResponse<any[]>>(`${environment.apiUrl}/auth/trusted-devices`);
  }

  removeTrustedDevice(deviceId: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${environment.apiUrl}/auth/trusted-devices/${deviceId}`);
  }

  verifyDevice(otp: string, sessionToken: string, trustDays = 30): Observable<ApiResponse<LoginResponse>> {
    return this.http.post<ApiResponse<LoginResponse>>(`${environment.apiUrl}/auth/device/verify`, { otp, sessionToken, trustDays }).pipe(
      tap(res => { if (res.success) this.storeSession(res.data); })
    );
  }

  // ── Security dashboard ────────────────────────────────────────────────
  getSecurityDashboard(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${environment.apiUrl}/auth/security-dashboard`);
  }

  /**
   * Called by SsoComponent after the nonce-redeem API call succeeds.
   * Stores the full session exactly the same way a normal login does.
   */
  storeSessionFromSso(data: LoginResponse): void {
    this.storeSession(data);
  }

  // ── Helpers ───────────────────────────────────────────────────────────
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getSessionId(): string | null {
    return localStorage.getItem(this.SESSION_KEY);
  }

  hasPermission(permission: string): boolean {
    return this._user()?.permissions.includes(permission) ?? false;
  }

  hasRole(role: string): boolean {
    return this._user()?.roles.includes(role) ?? false;
  }

  /** Patch in-memory user signal + localStorage after a profile update (no re-login needed) */
  patchUser(partial: Partial<Pick<AuthUser, 'firstName' | 'lastName' | 'email' | 'phoneNumber' | 'profileImagePath'>>): void {
    const current = this._user();
    if (!current) return;
    const updated: AuthUser = { ...current, ...partial };
    localStorage.setItem(this.USER_KEY, JSON.stringify(updated));
    this._user.set(updated);
  }

  private storeSession(data: LoginResponse): void {
    // Broadcast a login event only when it is a brand-new session (not a silent
    // token refresh) so other tabs know they should clear their stale session.
    const isNewSession = data.sessionId !== localStorage.getItem(this.SESSION_KEY);
    localStorage.setItem(this.TOKEN_KEY,   data.accessToken);
    localStorage.setItem(this.REFRESH_KEY, data.refreshToken);
    localStorage.setItem(this.SESSION_KEY, data.sessionId);
    const user: AuthUser = {
      id:               data.userId,
      userName:         data.userName,
      firstName:        data.firstName,
      lastName:         data.lastName,
      phoneNumber:      data.phoneNumber,
      email:            data.email,
      profileImagePath: data.profileImagePath,
      userLevel:        data.userLevel,
      roles:            data.roles,
      permissions:      data.permissions,
      sessionId:        data.sessionId
    };
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    this._user.set(user);
    if (isNewSession) {
      this.channel.postMessage({ type: 'login' });
    }
  }

  private loadUser(): AuthUser | null {
    const raw = localStorage.getItem(this.USER_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  }

  // ── Social Login ──────────────────────────────────────────────────────
  socialLogin(req: {
    provider: string; providerUserId: string; email?: string;
    displayName?: string; picture?: string; accessToken?: string; tokenExpiry?: string;
  }): Observable<ApiResponse<LoginResponse>> {
    return this.http.post<ApiResponse<LoginResponse>>(`${environment.apiUrl}/auth/social/callback`, req).pipe(
      tap(res => { if (res.success) this.storeSession(res.data); })
    );
  }

  getSocialConnections(): Observable<ApiResponse<any[]>> {
    return this.http.get<ApiResponse<any[]>>(`${environment.apiUrl}/auth/social/connections`);
  }

  disconnectSocialLogin(provider: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${environment.apiUrl}/auth/social/connections/${provider}`);
  }
}
